import { OrderStatus } from "common/types/orders-types";

export enum CancellationReason {
  NO_LONGER_NEEDED = "NO_LONGER_NEEDED",
  ORDERED_BY_MISTAKE = "ORDERED_BY_MISTAKE",
  SWITCHING_ASSET = "SWITCHING_ASSET",
  OTHER = "OTHER",
}

export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  [CancellationReason.NO_LONGER_NEEDED]: "No longer needed",
  [CancellationReason.ORDERED_BY_MISTAKE]: "Ordered by mistake",
  [CancellationReason.SWITCHING_ASSET]: "Switching to a different asset",
  [CancellationReason.OTHER]: "Other",
};

export const CANCELLABLE_STATUSES: OrderStatus[] = [OrderStatus.NEW, OrderStatus.PROCESSING];

export interface HopOrder {
  status?: string;
}

export interface OrderItemWithHopDetails {
  details?: {
    hopOrders?: HopOrder[];
  } | null;
}

export interface CancelOrderButtonProps {
  orderStatus: OrderStatus;
  orderId: string;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
  onConfirmCancellation: (reason: CancellationReason) => void;
  orderItems?: OrderItemWithHopDetails[];
}

export interface OrderDetails {
  orderId: string;
  orderReference: string;
  assetName: string;
  requestDate: string;
}

export interface CancellationState {
  isLoading: boolean;
  error: string | null;
}

export interface CancelOrderModalProps {
  onClose: () => void;
  onConfirm: (reason: CancellationReason) => void;
  orderDetails: OrderDetails;
  cancellationState: CancellationState;
}

export interface CancellationResult {
  success: boolean;
  error?: string;
}

export interface UseCancellationReturn {
  state: CancellationState;
  cancelOrder: (orderId: string, reason: CancellationReason) => Promise<boolean>;
  resetError: () => void;
}

export interface OrderCancellationIntegration {
  onOrderCancelled: (orderId: string) => void;
}

export const isCancellableStatus = (status: OrderStatus): boolean => {
  return CANCELLABLE_STATUSES.includes(status);
};

export const shouldHideCancelButton = (status: OrderStatus): boolean => {
  return status === OrderStatus.CANCELLED;
};

export const HOP_CANCELLABLE_STATUS = "ready-for-export";

/**
 * Checks whether all HOP orders across all items are in a cancellable state.
 * Returns true if no items have HOP orders or all HOP orders are "ready-for-export".
 * Returns false if any HOP order has a status other than "ready-for-export".
 */
export const isHopCancellable = (items: OrderItemWithHopDetails[]): boolean => {
  const allHopOrders = items.flatMap((item) => item.details?.hopOrders ?? []);
  return allHopOrders.every((hop) => !hop.status || hop.status === HOP_CANCELLABLE_STATUS);
};

/**
 * Maps ITO order status string to OrderStatus enum.
 * @param status - The ITO order status string
 * @returns The corresponding OrderStatus
 */
export const mapToOrderStatus = (status: string | undefined): OrderStatus => {
  switch (status?.toUpperCase()) {
    case "NEW":
      return OrderStatus.NEW;
    case "PROCESSING":
    case "PENDING_SHIPPING":
      return OrderStatus.PROCESSING;
    case "SHIPPED":
      return OrderStatus.SHIPPED;
    case "DELIVERED":
      return OrderStatus.DELIVERED;
    case "CANCELLED":
      return OrderStatus.CANCELLED;
    default:
      return OrderStatus.PROCESSING;
  }
};
